/**
 * Processes an image for efficient preview and AI extraction.
 * Returns both an Object URL (low memory) and a Base64 string (for API).
 */
export const processImage = (
  source: string | File | Blob, 
  maxWidth = 800
): Promise<{ previewUrl: string; base64: string; blob: Blob }> => {
  return new Promise((resolve, reject) => {
    // 1. INPUT VALIDATION: Ensure we have a valid source
    if (!source) {
      reject(new Error("No image source provided"));
      return;
    }

    const handleLoad = (dataUrl: string) => {
      if (!dataUrl || typeof dataUrl !== 'string') {
        reject(new Error("Invalid image data URL"));
        return;
      }

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // PIVOT: Use more aggressive compression for mobile performance
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { alpha: false }); // Disable alpha for better performance
        
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }
        
        ctx.fillStyle = "#FFFFFF"; // Ensure white background for JPEG
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error("Failed to create blob"));
            return;
          }
          
          const previewUrl = URL.createObjectURL(blob);
          const readerBase64 = new FileReader();
          readerBase64.onloadend = () => {
            const base64Full = readerBase64.result as string;
            const base64 = base64Full.split(',')[1];
            resolve({ previewUrl, base64, blob });
          };
          readerBase64.onerror = () => reject(new Error("Failed to convert blob to base64"));
          readerBase64.readAsDataURL(blob);
        }, 'image/jpeg', 0.7);
      };
      img.onerror = (e) => {
        console.error("[processImage] Image load failed:", e);
        reject(new Error("Failed to load image for processing"));
      };
      img.src = dataUrl;
    };

    if (source instanceof File || source instanceof Blob) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          handleLoad(result);
        } else {
          reject(new Error("FileReader did not return a string"));
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file/blob source"));
      reader.readAsDataURL(source);
    } else if (typeof source === 'string') {
      // If it's already a data URL, use it directly
      if (source.startsWith('data:')) {
        handleLoad(source);
      } else {
        // PIVOT: If it's a URL or path, we need to fetch it first to avoid CORS or protocol errors
        fetch(source)
          .then(res => res.blob())
          .then(blob => {
            const reader = new FileReader();
            reader.onload = (e) => handleLoad(e.target?.result as string);
            reader.readAsDataURL(blob);
          })
          .catch(err => {
            console.error("[processImage] Fetch failed for source:", source, err);
            reject(new Error(`Failed to fetch image source: ${source}`));
          });
      }
    } else {
      reject(new Error("Unsupported source type for processImage"));
    }
  });
};

/**
 * Legacy support for direct optimization.
 * Now enhanced with Web Worker offloading.
 */
export const optimizeImage = (source: string | Blob, maxWidth = 400): Promise<string> => {
  return new Promise((resolve) => {
    // 1. Try Web Worker first (OffscreenCanvas required)
    if (window.Worker && window.OffscreenCanvas) {
      const worker = new Worker('/imageWorker.js');
      
      worker.onmessage = (e) => {
        if (e.data.success) {
          resolve(`data:image/jpeg;base64,${e.data.base64}`);
        } else {
          mainThreadFallback();
        }
        worker.terminate();
      };

      worker.onerror = () => {
        mainThreadFallback();
        worker.terminate();
      };

      // Convert to blob for transfer if it's base64
      if (typeof source === 'string') {
        fetch(source.startsWith('data:') ? source : `data:image/jpeg;base64,${source}`)
          .then(res => res.blob())
          .then(blob => worker.postMessage({ blob, maxWidth, quality: 0.6 }))
          .catch(() => mainThreadFallback());
      } else {
        worker.postMessage({ blob: source, maxWidth, quality: 0.6 });
      }
      
      return;
    }

    // 2. Fallback to Main Thread
    function mainThreadFallback() {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(typeof source === 'string' ? source : URL.createObjectURL(source));
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.6));
      };
      img.onerror = () => resolve(typeof source === 'string' ? source : URL.createObjectURL(source));
      img.src = typeof source === 'string' 
        ? (source.startsWith('data:') ? source : `data:image/jpeg;base64,${source}`)
        : URL.createObjectURL(source);
    }

    if (!(window.Worker && window.OffscreenCanvas)) {
      mainThreadFallback();
    }
  });
};

export const dataURLToBlob = (dataURL: string): Blob => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)![1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};
